import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import { Input, Space, Button, Tag, Tooltip, Typography, DatePicker } from "antd";
import {
    SearchOutlined,
    MessageOutlined,
    PictureOutlined,
    UserOutlined,
    CalendarOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import dayjs from "dayjs";

import { IMessage } from "../../interfaces";
import { EmitError } from "../../utils/EmitError";
import { formatDateStringToDateAndTime } from "../../utils/Conversores";
import useAuth from "../../hooks/useAuth";
import MessagesService from "../../services/http/services/MessagesService";
import { getSortOrder } from "../../hooks/SortOrder";
import { ThemeContext } from "../../contexts/ThemeContext";
import { useTableParams } from "../../hooks/useTableParams";
import TableActionButton from "../../components/TableActionButton";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import { removeAcento } from "../../utils/RemoveAcentos";

const { Text } = Typography;

export interface ITableData extends IMessage {
    key: number | string;
    panel_user_name: string;
}

export type TypeSortMessage = 'title' | 'created_at' | 'panel_user_name' | 'type';

export interface IMessagesFilters {
    title: string | null;
    type: string | null;
    panel_user_name: string | null;
    created_at: string | null;
}

export default function useMessages() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawMessages, setRawMessages] = useState<Array<ITableData>>([]);
    const [searchParams, setSearchParams] = useSearchParams();

    const typeParam = searchParams.get('type');
    const isGeneral = useMemo(() => {
        return typeParam?.toLowerCase() === "general";
    }, [typeParam]);

    const navigate = useNavigate();
    const { activeCity } = useAuth();

    const {
        tableParams,
        handleTableChange,
        handleCustomChange,
        handlePaginationChange,
        handleFilterClear,
        handleClearAllFilters,
    } = useTableParams<IMessagesFilters, TypeSortMessage>({
        storageKey: isGeneral ? '@NotaMunicipio:generalMessagesFilters' : '@NotaMunicipio:cityMessagesFilters',
        defaultFilters: {
            title: null,
            type: null,
            panel_user_name: null,
            created_at: null,
        },
        defaultSort: {
            field: 'created_at',
            order: 'desc',
        },
        defaultPageSize: 25,
    });

    const [searchTitle, setSearchTitle] = useState(tableParams.filters.title || '');
    const [searchAuthor, setSearchAuthor] = useState(tableParams.filters.panel_user_name || '');
    const titleInputRef = useRef<any>(null);
    const authorInputRef = useRef<any>(null);

    useEffect(() => {
        setSearchTitle(tableParams.filters.title || '');
    }, [tableParams.filters.title]);

    useEffect(() => {
        setSearchAuthor(tableParams.filters.panel_user_name || '');
    }, [tableParams.filters.panel_user_name]);

    const handleClickEditMessage = useCallback((data: IMessage) => {
        navigate(`/mensagens/editar/${data.id}${isGeneral ? '?type=general' : ''}`);
    }, [navigate, isGeneral]);

    const handleClickCreateNewMessage = useCallback(() => {
        navigate(`/mensagens/criar${isGeneral ? '?type=general' : ''}`);
    }, [navigate, isGeneral]);

    const handleTabChange = useCallback((key: string) => {
        if (key === 'general') {
            setSearchParams({ type: 'general' });
        } else {
            setSearchParams({});
        }
    }, [setSearchParams]);

    // Active filters items for ActiveFiltersBar
    const activeFilterItems = useMemo<Array<IActiveFilterItem | null>>(() => {
        const typeLabels: Record<string, string> = {
            GENERAL: 'Geral',
            COUNTY: 'Município',
            DIRECT: 'Direta',
        };

        return [
            tableParams.filters.title
                ? {
                    key: 'title',
                    label: 'Título / Mensagem',
                    value: tableParams.filters.title,
                    onClear: () => handleFilterClear('title'),
                }
                : null,
            tableParams.filters.type
                ? {
                    key: 'type',
                    label: 'Tipo',
                    value: typeLabels[tableParams.filters.type] || tableParams.filters.type,
                    onClear: () => handleFilterClear('type'),
                }
                : null,
            tableParams.filters.panel_user_name
                ? {
                    key: 'panel_user_name',
                    label: 'Cadastrada por',
                    value: tableParams.filters.panel_user_name,
                    onClear: () => handleFilterClear('panel_user_name'),
                }
                : null,
            tableParams.filters.created_at
                ? {
                    key: 'created_at',
                    label: 'Data',
                    value: dayjs(tableParams.filters.created_at).format('DD/MM/YYYY'),
                    onClear: () => handleFilterClear('created_at'),
                }
                : null,
        ];
    }, [tableParams.filters, handleFilterClear]);

    // Load data from API
    useEffect(() => {
        if (!activeCity && !isGeneral) {
            return;
        }

        const controller = new AbortController();

        async function loadMessages() {
            try {
                setIsLoading(true);

                let messagesList: IMessage[] = [];
                if (isGeneral) {
                    messagesList = await MessagesService.listGeneralMessages(controller.signal);
                } else if (activeCity?.id) {
                    messagesList = await MessagesService.listCityMessages(activeCity.id, controller.signal);
                }

                const mappedData: ITableData[] = messagesList.map((message) => ({
                    ...message,
                    key: message.id,
                    panel_user_name: message.panel_user?.name || '',
                }));

                setRawMessages(mappedData);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'messages_load_error');
                setIsLoading(false);
            }
        }

        loadMessages();

        return () => {
            controller.abort();
        };
    }, [activeCity, isGeneral]);

    // Filter and sort data locally
    const filteredAndSortedData = useMemo(() => {
        let result = [...rawMessages];

        // Filter: Title
        if (tableParams.filters.title) {
            const query = removeAcento(tableParams.filters.title.toLowerCase().trim());
            result = result.filter((item) => {
                const titleMatch = removeAcento((item.title || '').toLowerCase()).includes(query);
                const contentMatch = removeAcento((item.content || '').toLowerCase()).includes(query);
                return titleMatch || contentMatch;
            });
        }

        // Filter: Type
        if (tableParams.filters.type) {
            result = result.filter((item) => item.type === tableParams.filters.type);
        }

        // Filter: Author (panel_user_name)
        if (tableParams.filters.panel_user_name) {
            const query = removeAcento(tableParams.filters.panel_user_name.toLowerCase().trim());
            result = result.filter((item) =>
                removeAcento((item.panel_user_name || '').toLowerCase()).includes(query)
            );
        }

        // Filter: Date
        if (tableParams.filters.created_at) {
            const targetDate = dayjs(tableParams.filters.created_at).format('YYYY-MM-DD');
            result = result.filter((item) => {
                if (!item.created_at) return false;
                const itemDate = dayjs(item.created_at).format('YYYY-MM-DD');
                return itemDate === targetDate;
            });
        }

        // Sort
        const sortField = tableParams.sorter.field;
        const sortOrder = tableParams.sorter.order;

        if (sortField) {
            result.sort((a, b) => {
                let comparison = 0;
                if (sortField === 'title') {
                    comparison = (a.title || '').localeCompare(b.title || '');
                } else if (sortField === 'panel_user_name') {
                    comparison = (a.panel_user_name || '').localeCompare(b.panel_user_name || '');
                } else if (sortField === 'type') {
                    comparison = (a.type || '').localeCompare(b.type || '');
                } else if (sortField === 'created_at') {
                    const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
                    comparison = timeA - timeB;
                }
                return sortOrder === 'desc' ? -comparison : comparison;
            });
        }

        return result;
    }, [rawMessages, tableParams.filters, tableParams.sorter]);

    // Paginated slice
    const tableData = useMemo(() => {
        const { current, pageSize } = tableParams.pagination;
        const startIndex = (current - 1) * pageSize;
        return filteredAndSortedData.slice(startIndex, startIndex + pageSize);
    }, [filteredAndSortedData, tableParams.pagination]);

    const totalItems = filteredAndSortedData.length;
    const rawTotalCount = rawMessages.length;

    // Table columns definition
    const columns: any[] = useMemo(() => {
        return [
            {
                title: 'Título / Mensagem',
                dataIndex: 'title',
                key: 'title',
                sorter: true,
                showSorterTooltip: false,
                sortOrder: getSortOrder('title', tableParams.sorter),
                filterDropdown: ({ confirm, clearFilters }: any) => (
                    <div style={{ padding: 12, width: 280, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                                Buscar por Título ou Conteúdo
                            </div>
                            <Input
                                ref={titleInputRef}
                                placeholder="Digite para filtrar..."
                                value={searchTitle}
                                onChange={(e) => setSearchTitle(e.target.value)}
                                onPressEnter={() => {
                                    confirm();
                                    handleCustomChange({
                                        newPage: 1,
                                        newFilters: { title: searchTitle.trim() || null },
                                    });
                                }}
                                allowClear
                            />
                        </div>
                        <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                size="small"
                                onClick={() => {
                                    confirm();
                                    handleCustomChange({
                                        newPage: 1,
                                        newFilters: { title: searchTitle.trim() || null },
                                    });
                                }}
                            >
                                Filtrar
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    setSearchTitle('');
                                    clearFilters?.();
                                    handleFilterClear('title');
                                }}
                            >
                                Limpar
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered: boolean) => (
                    <SearchOutlined style={{ color: tableParams.filters.title ? '#1677ff' : undefined }} />
                ),
                render: (_: any, record: ITableData) => (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                background: isDark ? 'rgba(22, 119, 255, 0.15)' : '#e6f4ff',
                                color: '#1677ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 18,
                                flexShrink: 0,
                                marginTop: 2,
                            }}
                        >
                            <MessageOutlined />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Text
                                    strong
                                    style={{
                                        fontSize: 14,
                                        color: isDark ? '#f0f0f0' : '#1e293b',
                                    }}
                                >
                                    <Highlighter
                                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                        searchWords={[tableParams.filters.title || '']}
                                        autoEscape
                                        textToHighlight={record.title || ''}
                                    />
                                </Text>
                                {record.image && (
                                    <Tooltip title="Possui imagem anexada">
                                        <Tag
                                            icon={<PictureOutlined />}
                                            color="processing"
                                            style={{ margin: 0, fontSize: 11, borderRadius: 4 }}
                                        >
                                            Imagem
                                        </Tag>
                                    </Tooltip>
                                )}
                            </div>
                            {record.content && (
                                <Text
                                    type="secondary"
                                    ellipsis
                                    style={{
                                        fontSize: 12,
                                        maxWidth: 480,
                                        color: isDark ? '#a0aec0' : '#64748b',
                                    }}
                                >
                                    <Highlighter
                                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                        searchWords={[tableParams.filters.title || '']}
                                        autoEscape
                                        textToHighlight={record.content}
                                    />
                                </Text>
                            )}
                        </div>
                    </div>
                ),
            },
            {
                title: 'Tipo',
                dataIndex: 'type',
                key: 'type',
                width: 140,
                sorter: true,
                showSorterTooltip: false,
                sortOrder: getSortOrder('type', tableParams.sorter),
                filters: [
                    { text: 'Geral', value: 'GENERAL' },
                    { text: 'Município', value: 'COUNTY' },
                    { text: 'Direta', value: 'DIRECT' },
                ],
                filteredValue: tableParams.filters.type ? [tableParams.filters.type] : null,
                render: (type: string) => {
                    if (type === 'GENERAL') {
                        return <Tag color="purple" style={{ fontWeight: 600, borderRadius: 4 }}>Geral</Tag>;
                    }
                    if (type === 'DIRECT') {
                        return <Tag color="cyan" style={{ fontWeight: 600, borderRadius: 4 }}>Direta</Tag>;
                    }
                    return <Tag color="blue" style={{ fontWeight: 600, borderRadius: 4 }}>Município</Tag>;
                },
            },
            {
                title: 'Cadastrada por',
                dataIndex: 'panel_user_name',
                key: 'panel_user_name',
                width: 220,
                sorter: true,
                showSorterTooltip: false,
                sortOrder: getSortOrder('panel_user_name', tableParams.sorter),
                filterDropdown: ({ confirm, clearFilters }: any) => (
                    <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                                Nome do Usuário
                            </div>
                            <Input
                                ref={authorInputRef}
                                placeholder="Filtrar por autor..."
                                value={searchAuthor}
                                onChange={(e) => setSearchAuthor(e.target.value)}
                                onPressEnter={() => {
                                    confirm();
                                    handleCustomChange({
                                        newPage: 1,
                                        newFilters: { panel_user_name: searchAuthor.trim() || null },
                                    });
                                }}
                                allowClear
                            />
                        </div>
                        <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                size="small"
                                onClick={() => {
                                    confirm();
                                    handleCustomChange({
                                        newPage: 1,
                                        newFilters: { panel_user_name: searchAuthor.trim() || null },
                                    });
                                }}
                            >
                                Filtrar
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    setSearchAuthor('');
                                    clearFilters?.();
                                    handleFilterClear('panel_user_name');
                                }}
                            >
                                Limpar
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered: boolean) => (
                    <SearchOutlined style={{ color: tableParams.filters.panel_user_name ? '#1677ff' : undefined }} />
                ),
                render: (name: string) => (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <UserOutlined style={{ color: isDark ? '#718096' : '#94a3b8', fontSize: 13 }} />
                        <Text style={{ fontSize: 13, color: isDark ? '#e2e8f0' : '#334155' }}>
                            <Highlighter
                                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                searchWords={[tableParams.filters.panel_user_name || '']}
                                autoEscape
                                textToHighlight={name || '—'}
                            />
                        </Text>
                    </span>
                ),
            },
            {
                title: 'Data de Cadastro',
                dataIndex: 'created_at',
                key: 'created_at',
                width: 200,
                sorter: true,
                showSorterTooltip: false,
                sortOrder: getSortOrder('created_at', tableParams.sorter),
                filterDropdown: ({ confirm, clearFilters }: any) => (
                    <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                                Selecionar Data
                            </div>
                            <DatePicker
                                format="DD/MM/YYYY"
                                style={{ width: '100%' }}
                                value={tableParams.filters.created_at ? dayjs(tableParams.filters.created_at) : null}
                                onChange={(date) => {
                                    confirm();
                                    handleCustomChange({
                                        newPage: 1,
                                        newFilters: {
                                            created_at: date ? date.format('YYYY-MM-DD') : null,
                                        },
                                    });
                                }}
                            />
                        </div>
                        <Space style={{ justifyContent: 'flex-end', marginTop: 4 }}>
                            <Button
                                size="small"
                                onClick={() => {
                                    clearFilters?.();
                                    handleFilterClear('created_at');
                                }}
                            >
                                Limpar
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered: boolean) => (
                    <CalendarOutlined style={{ color: tableParams.filters.created_at ? '#1677ff' : undefined }} />
                ),
                render: (created: string) => (
                    <Text
                        style={{
                            fontSize: 13,
                            color: isDark ? '#cbd5e1' : '#475569',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {created ? formatDateStringToDateAndTime(created) : '—'}
                    </Text>
                ),
            },
            {
                title: 'Ações',
                key: 'actions',
                width: 90,
                align: 'center',
                render: (_: any, record: ITableData) => (
                    <TableActionButton
                        action="edit"
                        tooltip="Editar Mensagem"
                        onClick={() => handleClickEditMessage(record)}
                    />
                ),
            },
        ];
    }, [
        tableParams,
        isDark,
        searchTitle,
        searchAuthor,
        handleCustomChange,
        handleFilterClear,
        handleClickEditMessage,
    ]);

    return {
        isLoading,
        isGeneral,
        columns,
        tableData,
        totalItems,
        rawTotalCount,
        tableParams,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        handleClickCreateNewMessage,
        handleClickEditMessage,
        handleTabChange,
        navigate,
    };
}
